import { effectiveSplit, type Order } from "../shared/order-domain";
import {
  buildStatsSummary,
  getOrdersInRange,
  orderStatsDate,
  statsDateKey,
  statsDelta,
  statsRangeBounds,
  STATS_RANGES,
  sumOrderRevenue,
  type StatsGrouping,
  type StatsRangeKey,
} from "../shared/stats-domain";
import { isFriend, state } from "./state";
import {
  bindModalBackdrop,
  closeModal,
  element,
  escapeHtml,
  formatDayShort,
  openModal,
} from "./ui";

interface TrendPoint {
  label: string;
  dateLabel: string;
  value: number;
  count: number;
}

interface StatsViewState {
  rangeKey: StatsRangeKey;
  grouping: StatsGrouping;
  series: TrendPoint[];
}

export interface StatsActions {
  transfer(): void;
  undoTransfer(): void;
}

let view: StatsViewState | null = null;
let resizeTimer: number | null = null;

function deltaHtml(delta: number | null, label = "较前周期"): string {
  if (delta === null) return `<span class="kpi-delta kpi-delta-flat">${label}无数据</span>`;
  const positive = delta >= 0;
  return `<span class="kpi-delta ${positive ? "kpi-delta-up" : "kpi-delta-down"}">${label} ${positive ? "▲" : "▼"}${Math.abs(delta).toFixed(1)}%</span>`;
}

export function renderStatsCards(): void {
  const current = getOrdersInRange(state.orders, "30");
  const previous = getOrdersInRange(state.orders, "30", new Date(), 1);
  const revenue = sumOrderRevenue(current);
  const revenueDelta = statsDelta(revenue, sumOrderRevenue(previous));
  const untransferred = state.orders.filter((order) => !order.is_transferred);
  const owed = untransferred.reduce((sum, order) => sum + effectiveSplit(order), 0);
  const unprintedFiles = state.orders.reduce(
    (sum, order) => sum + order.items.filter((item) => !item.is_printed).length,
    0,
  );
  const unprintedOrders = state.orders.filter((order) =>
    order.items.some((item) => !item.is_printed),
  ).length;
  const transferTimes = state.orders
    .map((order) => order.transferred_at)
    .filter((time): time is string => Boolean(time))
    .sort();
  const lastSettled = transferTimes.length
    ? formatDayShort(transferTimes[transferTimes.length - 1])
    : "";
  let transferAction = "";
  if (untransferred.length === 0) {
    transferAction = lastSettled
      ? `<div class="settled-cluster"><div class="settled-note">✓ 已全部结清（上次 ${lastSettled}）</div>${isFriend() ? '<button class="btn btn-sm card-btn btn-ghost" id="undoTransferBtn" title="撤销最近一次全部转账">撤销</button>' : ""}</div>`
      : '<div class="settled-note">✓ 已全部结清</div>';
  } else if (isFriend()) {
    transferAction = '<button class="btn btn-sm card-btn" id="markAllBtn">标记全部已转账</button>';
  }
  element<HTMLElement>("stats").innerHTML = `<button type="button" class="stat stat-rel stat-clickable" id="statsCardRevenue"><span class="info-btn" id="statsInfoBtn" title="统计详情">i</span><div class="label">近 30 天流水 · 点击查看收益预览</div><div class="value money">${revenue.toFixed(2)}</div><div class="stat-sub">${current.length} 单 ${deltaHtml(revenueDelta, "较前 30 天")}</div></button><div class="stat stat-transfer"><div class="stat-left"><div class="label">待转账给朋友（累计 ${untransferred.length} 单）</div><div class="value money">${owed.toFixed(2)}</div></div><div class="stat-right">${transferAction}</div></div><div class="stat"><div class="label">待打印（${unprintedOrders} 单 / ${unprintedFiles} 个文件）</div><div class="value">${unprintedFiles} <span class="muted" style="font-size:14px">个</span></div></div>`;
}

function trendSeries(orders: readonly Order[], rangeKey: StatsRangeKey): TrendPoint[] {
  const configuration = STATS_RANGES[rangeKey];
  const valid = orders
    .map((order) => ({ order, date: orderStatsDate(order) }))
    .filter((entry): entry is { order: Order; date: Date } => Boolean(entry.date));
  if (configuration.grouping === "day") {
    const buckets = new Map<string, { value: number; count: number }>();
    for (const { order, date } of valid) {
      const key = statsDateKey(date);
      const bucket = buckets.get(key) ?? { value: 0, count: 0 };
      bucket.value += order.total_price ?? 0;
      bucket.count += 1;
      buckets.set(key, bucket);
    }
    const bounds = statsRangeBounds(rangeKey as Exclude<StatsRangeKey, "all">);
    return Array.from({ length: configuration.days }, (_, index) => {
      const date = new Date(bounds.start);
      date.setDate(date.getDate() + index);
      const bucket = buckets.get(statsDateKey(date)) ?? { value: 0, count: 0 };
      return {
        label: `${date.getMonth() + 1}/${date.getDate()}`,
        dateLabel: `${date.getMonth() + 1}月${date.getDate()}日`,
        ...bucket,
      };
    });
  }
  if (configuration.grouping === "week") {
    const bounds = statsRangeBounds(rangeKey as Exclude<StatsRangeKey, "all">);
    const count = Math.ceil(configuration.days / 7);
    const points = Array.from({ length: count }, (_, index): TrendPoint => {
      const date = new Date(bounds.start);
      date.setDate(date.getDate() + index * 7);
      return {
        label: `${date.getMonth() + 1}/${date.getDate()}`,
        dateLabel: `${date.getMonth() + 1}月${date.getDate()}日起`,
        value: 0,
        count: 0,
      };
    });
    for (const { order, date } of valid) {
      const index = Math.min(
        count - 1,
        Math.max(0, Math.floor((date.getTime() - bounds.start.getTime()) / 86_400_000 / 7)),
      );
      points[index].value += order.total_price ?? 0;
      points[index].count += 1;
    }
    return points;
  }
  const today = new Date();
  const first = valid.length
    ? valid.reduce((earliest, entry) => (entry.date < earliest ? entry.date : earliest), valid[0].date)
    : today;
  const buckets = new Map<string, { value: number; count: number }>();
  for (const { order, date } of valid) {
    const key = `${date.getFullYear()}-${date.getMonth()}`;
    const bucket = buckets.get(key) ?? { value: 0, count: 0 };
    bucket.value += order.total_price ?? 0;
    bucket.count += 1;
    buckets.set(key, bucket);
  }
  const points: TrendPoint[] = [];
  const firstMonth = new Date(first.getFullYear(), first.getMonth(), 1);
  for (let date = firstMonth; date <= today; date = new Date(date.getFullYear(), date.getMonth() + 1, 1)) {
    const bucket = buckets.get(`${date.getFullYear()}-${date.getMonth()}`) ?? {
      value: 0,
      count: 0,
    };
    points.push({
      label: `${date.getMonth() + 1}月`,
      dateLabel: `${date.getFullYear()}年${date.getMonth() + 1}月`,
      ...bucket,
    });
  }
  return points;
}

function renderStatsContent(rangeKey: StatsRangeKey): void {
  const configuration = STATS_RANGES[rangeKey];
  const orders = getOrdersInRange(state.orders, rangeKey);
  const previous = getOrdersInRange(state.orders, rangeKey, new Date(), 1);
  const summary = buildStatsSummary(state.orders, rangeKey);
  const noComparison = rangeKey === "all";
  const revenueDelta = statsDelta(summary.revenue, sumOrderRevenue(previous));
  const countDelta = statsDelta(summary.orderCount, previous.length);
  const colorQuantities = new Map<string, number>();
  let totalFiles = 0;
  let totalQuantity = 0;
  let whiteQuantity = 0;
  for (const order of state.orders) {
    for (const item of order.items) {
      totalFiles += 1;
      totalQuantity += item.quantity;
      const color = item.color || "未填";
      colorQuantities.set(color, (colorQuantities.get(color) ?? 0) + item.quantity);
      if (color.includes("白")) whiteQuantity += item.quantity;
    }
  }
  const colors = [...colorQuantities.entries()].sort((left, right) => right[1] - left[1]);
  const maximum = Math.max(1, ...colors.map(([, quantity]) => quantity));
  const colorRows = colors
    .map(
      ([color, quantity]) => `<div class="cd-row2${color.includes("白") ? " cd-white" : ""}"><span class="cd-name" title="${escapeHtml(color)}">${escapeHtml(color)}</span><div class="cd-bar-wrap"><div class="cd-bar" style="width:${((quantity / maximum) * 100).toFixed(1)}%"></div></div><span class="cd-num">${quantity}</span></div>`,
    )
    .join("");
  const buttons = (Object.entries(STATS_RANGES) as Array<[StatsRangeKey, (typeof STATS_RANGES)[StatsRangeKey]]>)
    .map(
      ([key, range]) => `<button type="button" class="seg-btn${key === rangeKey ? " is-active" : ""}" data-range="${key}">${key === "all" ? "全部" : `近${range.days}天`}</button>`,
    )
    .join("");
  const allTime = '<span class="kpi-delta kpi-delta-flat">全部时间</span>';
  element<HTMLElement>("statsContent").innerHTML = `<div class="stats-range-head"><div><h3 class="stats-h3">经营指标</h3><div class="stats-range-caption">${configuration.label} · ${noComparison ? "全部时间" : `对比${configuration.compare}`}</div></div><div class="seg stats-range-seg" id="statsRangeToggle">${buttons}</div></div><div class="kpi-grid"><div class="kpi"><div class="kpi-label">期间流水</div><div class="kpi-val">¥${summary.revenue.toFixed(2)}</div><div class="kpi-sub">${noComparison ? allTime : deltaHtml(revenueDelta)}</div></div><div class="kpi"><div class="kpi-label">订单数</div><div class="kpi-val">${summary.orderCount} 单</div><div class="kpi-sub">${noComparison ? allTime : deltaHtml(countDelta)}</div></div><div class="kpi"><div class="kpi-label">日均流水</div><div class="kpi-val">¥${summary.dailyRevenue.toFixed(2)}</div><div class="kpi-sub">包含无订单日期</div></div><div class="kpi"><div class="kpi-label">活跃出单天数</div><div class="kpi-val">${summary.activeDays} 天</div><div class="kpi-sub">${((summary.activeDays / summary.periodDays) * 100).toFixed(0)}% 的日期有订单</div></div><div class="kpi"><div class="kpi-label">期间应分成</div><div class="kpi-val">¥${summary.totalSplit.toFixed(2)}</div><div class="kpi-sub">按实际分成金额</div></div><div class="kpi"><div class="kpi-label">Sky赚到的钱</div><div class="kpi-val">¥${summary.earned.toFixed(2)}</div><div class="kpi-sub">已扣朋友分成、服务费及运费</div></div><div class="kpi"><div class="kpi-label">运费</div><div class="kpi-val">¥${summary.shippingCost.toFixed(2)}</div><div class="kpi-sub">按订单计算</div></div><div class="kpi"><div class="kpi-label">闲鱼服务费</div><div class="kpi-val">¥${summary.serviceFee.toFixed(2)}</div><div class="kpi-sub">按期间流水计算</div></div></div><div class="stats-detail-grid"><section class="stats-panel stats-trend-panel"><div class="trend-head"><div><h3 class="stats-h3">流水趋势</h3><div class="stats-range-caption">${configuration.grouping === "day" ? "按日" : configuration.grouping === "week" ? "按周" : "按月"} · 橙线为移动平均</div></div></div><div id="trendChart" class="trend-chart"></div></section><section class="stats-panel stats-color-panel"><h3 class="stats-h3">颜色分布（按个数）</h3><div class="color-dist">${colorRows || '<span class="muted">暂无</span>'}</div><div class="white-share">白色料占比 <strong>${totalQuantity ? ((whiteQuantity / totalQuantity) * 100).toFixed(0) : "0"}%</strong>（${whiteQuantity} / ${totalQuantity} 个）<span class="muted"> · 备白料参考</span></div><div class="stats-note">平均每单 <strong>${state.orders.length ? (totalFiles / state.orders.length).toFixed(1) : "0.0"}</strong> 个文件</div></section></div>`;
  view = { rangeKey, grouping: configuration.grouping, series: trendSeries(orders, rangeKey) };
  renderChart();
}

function renderChart(): void {
  const host = document.getElementById("trendChart");
  if (!host || !view || view.series.length === 0) return;
  if (!host.clientWidth) {
    window.requestAnimationFrame(renderChart);
    return;
  }
  const series = view.series;
  const width = Math.max(280, Math.round(host.clientWidth));
  const height = 210;
  const left = 46;
  const right = 10;
  const top = 14;
  const bottom = 24;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const baseline = top + plotHeight;
  const maximum = Math.max(1, ...series.map((point) => point.value));
  const step = plotWidth / series.length;
  const barWidth = Math.max(2, step * 0.62);
  const x = (index: number) => left + index * step + step / 2;
  const y = (value: number) => top + plotHeight - (value / maximum) * plotHeight;
  const averageWindow = view.grouping === "day" ? 7 : 4;
  const averages = series.map((_, index) => {
    const values = series.slice(Math.max(0, index - averageWindow + 1), index + 1);
    return values.reduce((sum, point) => sum + point.value, 0) / values.length;
  });
  const grids = [0, 0.5, 1]
    .map((fraction) => {
      const value = maximum * fraction;
      const position = y(value).toFixed(1);
      return `<line class="tc-grid" x1="${left}" y1="${position}" x2="${width - right}" y2="${position}"/><text class="tc-ylab" x="${left - 7}" y="${Number(position) + 4}" text-anchor="end">¥${value.toFixed(0)}</text>`;
    })
    .join("");
  const bars = series
    .map((point, index) => {
      const position = y(point.value);
      return `<rect class="tc-bar" x="${(x(index) - barWidth / 2).toFixed(1)}" y="${position.toFixed(1)}" width="${barWidth.toFixed(1)}" height="${Math.max(1, baseline - position).toFixed(1)}"/>`;
    })
    .join("");
  const averagePath = averages
    .map((value, index) => `${index ? "L" : "M"}${x(index).toFixed(1)} ${y(value).toFixed(1)}`)
    .join(" ");
  const labelStep = Math.max(1, Math.ceil(series.length / 6));
  const labels = series
    .map((point, index) =>
      index % labelStep === 0 || index === series.length - 1
        ? `<text class="tc-xlab" x="${x(index).toFixed(1)}" y="${height - 6}" text-anchor="middle">${escapeHtml(point.label)}</text>`
        : "",
    )
    .join("");
  host.innerHTML = `<svg class="tc-svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}" role="img" aria-label="流水趋势图">${grids}${bars}<path class="tc-avg" d="${averagePath}"/>${labels}<line class="tc-cross" x1="0" y1="${top}" x2="0" y2="${baseline}" style="display:none"/><circle class="tc-cursor" r="4" style="display:none"/><rect class="tc-hit" x="${left}" y="${top}" width="${plotWidth}" height="${plotHeight}" fill="transparent"/></svg><div class="tc-tip" style="display:none"></div>`;
  const svg = host.querySelector<SVGSVGElement>(".tc-svg");
  const hit = host.querySelector<SVGRectElement>(".tc-hit");
  const cross = host.querySelector<SVGLineElement>(".tc-cross");
  const cursor = host.querySelector<SVGCircleElement>(".tc-cursor");
  const tip = host.querySelector<HTMLElement>(".tc-tip");
  if (!svg || !hit || !cross || !cursor || !tip) return;
  const show = (clientX: number) => {
    const rect = svg.getBoundingClientRect();
    const position = ((clientX - rect.left) * width) / (rect.width || width);
    const index = Math.max(0, Math.min(series.length - 1, Math.floor((position - left) / step)));
    const point = series[index];
    const pointX = x(index);
    const pointY = y(point.value);
    cross.setAttribute("x1", String(pointX));
    cross.setAttribute("x2", String(pointX));
    cursor.setAttribute("cx", String(pointX));
    cursor.setAttribute("cy", String(pointY));
    cross.style.display = "";
    cursor.style.display = "";
    tip.innerHTML = `<div class="tc-tip-d">${escapeHtml(point.dateLabel)}</div><div class="tc-tip-v">¥${point.value.toFixed(2)}</div><div class="tc-tip-c">${point.count} 单</div>`;
    tip.style.display = "";
    const tipWidth = tip.offsetWidth || 96;
    tip.style.left = `${Math.max(2, Math.min(host.clientWidth - tipWidth - 2, (pointX / width) * host.clientWidth - tipWidth / 2))}px`;
    tip.style.top = "0px";
  };
  const hide = () => {
    cross.style.display = "none";
    cursor.style.display = "none";
    tip.style.display = "none";
  };
  hit.addEventListener("mousemove", (event) => show(event.clientX));
  hit.addEventListener("mouseleave", hide);
  hit.addEventListener("touchstart", (event) => event.touches[0] && show(event.touches[0].clientX), { passive: true });
  hit.addEventListener("touchmove", (event) => event.touches[0] && show(event.touches[0].clientX), { passive: true });
}

export function openStats(): void {
  const modal = element<HTMLElement>("statsModal");
  openModal(modal);
  renderStatsContent(getOrdersInRange(state.orders, "30").length ? "30" : "all");
}

export function initStats(actions: StatsActions): void {
  const stats = element<HTMLElement>("stats");
  stats.addEventListener("click", (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (target?.closest("#markAllBtn")) actions.transfer();
    else if (target?.closest("#undoTransferBtn")) actions.undoTransfer();
    else if (target?.closest("#statsCardRevenue")) openStats();
  });
  const modal = element<HTMLElement>("statsModal");
  element<HTMLButtonElement>("statsCloseBtn").addEventListener("click", () => closeModal(modal));
  bindModalBackdrop(modal, () => closeModal(modal));
  element<HTMLElement>("statsContent").addEventListener("click", (event) => {
    const button = event.target instanceof Element
      ? event.target.closest<HTMLButtonElement>("[data-range]")
      : null;
    const range = button?.dataset.range as StatsRangeKey | undefined;
    if (range && range !== view?.rangeKey && range in STATS_RANGES) renderStatsContent(range);
  });
  window.addEventListener("resize", () => {
    if (modal.hidden || !view) return;
    if (resizeTimer !== null) window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(renderChart, 120);
  });
}
